import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  onSnapshot,
  setDoc,
  doc,
  deleteDoc,
  getDocs
} from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import { Floor } from "./ManagerFloorView";
import { FiCalendar, FiCheckCircle, FiInfo, FiLayers, FiChevronLeft, FiChevronRight, FiEye, FiEyeOff, FiUsers, FiPlus, FiTrash2, FiMousePointer, FiLock } from "react-icons/fi";
import { IconWrapper } from "../../IconWrapper";
import { Switch, useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../../utils/themeUtils";
import { showToast } from "../../../utils/toastUtils";

interface FloorAssignment {
  id: string;
  date: string;
  floorId: string;
  availableSeats: number;
  canView: boolean;      // Users can see availability
  canBook: boolean;      // Users can book seats
}

interface FloorAssignmentManagerProps {
  currentOrgId: string;
  floors: Floor[];
}

const FloorAssignmentManager: React.FC<FloorAssignmentManagerProps> = ({ currentOrgId, floors }) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const theme = getThemeClasses(isDark);

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [assignments, setAssignments] = useState<FloorAssignment[]>([]);
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Form state for adding new assignment
  const [selectedFloorId, setSelectedFloorId] = useState<string>("");
  const [availableSeats, setAvailableSeats] = useState<number>(0);
  const [canView, setCanView] = useState<boolean>(true);
  const [canBook, setCanBook] = useState<boolean>(true);


  const toLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const dateStr = toLocalDateString(selectedDate);
  const dateAssignments = assignments.filter(a => a.date === dateStr);

  useEffect(() => {
    if (!currentOrgId) return;

    const ref = collection(db, `organization/${currentOrgId}/floor_assignments`);
    const unsubscribe = onSnapshot(ref, (snap) => {
      const data: FloorAssignment[] = [];
      snap.docs.forEach(d => {
        const assignData = d.data();
        data.push({
          id: d.id,
          date: assignData.date,
          floorId: assignData.floorId,
          availableSeats: assignData.availableSeats || 0,
          canView: assignData.canView ?? true,
          canBook: assignData.canBook ?? true
        });
      });
      setAssignments(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentOrgId]);

  // Fetch bookings for statistics
  useEffect(() => {
    if (!currentOrgId) return;

    const bookingsRef = collection(db, `organization/${currentOrgId}/bookings`);
    const unsubscribe = onSnapshot(bookingsRef, (snap) => {
      const data: any[] = [];
      snap.docs.forEach(d => {
        data.push({ id: d.id, ...d.data() });
      });
      setBookings(data);
    });

    return () => unsubscribe();
  }, [currentOrgId]);

  const handleAddAssignment = async () => {
    if (!selectedFloorId) {
      showToast({ title: "Please select a floor", status: "warning" });
      return;
    }

    if (availableSeats <= 0) {
      showToast({ title: "Please set available seats", status: "warning" });
      return;
    }

    // Check if this floor is already assigned to this date
    if (dateAssignments.some(a => a.floorId === selectedFloorId)) {
      showToast({ title: "This floor is already assigned to this date", status: "warning" });
      return;
    }

    try {
      const assignmentId = `${dateStr}_${selectedFloorId}`;
      await setDoc(doc(db, `organization/${currentOrgId}/floor_assignments`, assignmentId), {
        date: dateStr,
        floorId: selectedFloorId,
        availableSeats,
        canView,
        canBook
      });
      showToast({ title: "Floor added to date", status: "success" });

      // Reset form
      setSelectedFloorId("");
      setAvailableSeats(0);
      setCanView(true);
      setCanBook(true);
    } catch (e) {
      console.error("Assignment error", e);
      showToast({ title: "Failed to add assignment", status: "error" });
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    try {
      await deleteDoc(doc(db, `organization/${currentOrgId}/floor_assignments`, assignmentId));
      showToast({ title: "Assignment removed", status: "info" });
    } catch (e) {
      showToast({ title: "Failed to remove assignment", status: "error" });
    }
  };

  const handleToggleView = async (assignment: FloorAssignment) => {
    try {
      await setDoc(doc(db, `organization/${currentOrgId}/floor_assignments`, assignment.id), {
        ...assignment,
        canView: !assignment.canView
      });
    } catch (e) {
      showToast({ title: "Failed to update visibility", status: "error" });
    }
  };

  const handleToggleBook = async (assignment: FloorAssignment) => {
    try {
      await setDoc(doc(db, `organization/${currentOrgId}/floor_assignments`, assignment.id), {
        ...assignment,
        canBook: !assignment.canBook
      });
    } catch (e) {
      showToast({ title: "Failed to update booking permission", status: "error" });
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const changeMonth = (offset: number) => {
    const next = new Date(selectedDate);
    next.setMonth(next.getMonth() + offset);
    setSelectedDate(next);
  };

  const changeYear = (offset: number) => {
    const next = new Date(selectedDate);
    next.setFullYear(next.getFullYear() + offset);
    setSelectedDate(next);
  };

  const getDateAssignmentCount = (dateStr: string) => {
    return assignments.filter(a => a.date === dateStr).length;
  };

  const selectedFloor = floors.find(f => f.id === selectedFloorId);
  const maxSeats = selectedFloor?.seats?.length || 0;

  // Get floors not yet assigned to this date AND are active
  const availableFloorsForDate = floors.filter(f =>
    !dateAssignments.some(a => a.floorId === f.id) && f.isActive
  );

  // Calculate monthly statistics
  const currentMonth = selectedDate.getMonth();
  const currentYear = selectedDate.getFullYear();

  const monthlyAssignments = assignments.filter(a => {
    const assignDate = new Date(a.date);
    return assignDate.getMonth() === currentMonth && assignDate.getFullYear() === currentYear;
  });

  const monthlyBookings = bookings.filter(b => {
    if (!b.date || b.status === 'cancelled') return false;
    const bookingDate = new Date(b.date);
    return bookingDate.getMonth() === currentMonth && bookingDate.getFullYear() === currentYear;
  });

  return (
    <div className="flex flex-col gap-4 animate-in fade-in duration-300">
      {/* Top Section: Stats Cards (Left 30%) and Calendar (Right 70%) */}
      <div className="grid grid-cols-1 lg:grid-cols-[30%_70%] gap-4">
        {/* Left: Monthly Statistics Cards */}
        <div className="flex flex-col gap-4">
          {/* Total Assignments Card */}
          <div className={`${theme.card} border ${theme.border} rounded-2xl p-4 relative overflow-hidden group hover:shadow-md transition-all`}>
            <div className={`absolute top-0 right-0 w-20 h-20 rounded-full blur-2xl opacity-20 ${isDark ? 'bg-[#B59143]' : 'bg-[#FBD282]'} group-hover:opacity-30 transition-opacity`}></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-lg ${isDark ? 'bg-[#B59143]/20' : 'bg-[#FBD282]/30'}`}>
                  <IconWrapper icon={FiLayers} size={18} className="text-[#B59143]" />
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>This Month</span>
              </div>
              <h3 className={`text-3xl font-black ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>{monthlyAssignments.length}</h3>
              <p className={`text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Assignments</p>
              <div className={`mt-3 pt-3 border-t ${isDark ? 'border-[#2E2F33]' : 'border-gray-100'}`}>
                <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'} font-medium`}>
                  {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>
          </div>

          {/* Total Bookings Card */}
          <div className={`${theme.card} border ${theme.border} rounded-2xl p-4 relative overflow-hidden group hover:shadow-md transition-all`}>
            <div className={`absolute top-0 right-0 w-20 h-20 rounded-full blur-2xl opacity-20 ${isDark ? 'bg-blue-500' : 'bg-blue-400'} group-hover:opacity-30 transition-opacity`}></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-lg ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                  <IconWrapper icon={FiUsers} size={18} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>This Month</span>
              </div>
              <h3 className={`text-3xl font-black ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>{monthlyBookings.length}</h3>
              <p className={`text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Bookings</p>
              <div className={`mt-3 pt-3 border-t ${isDark ? 'border-[#2E2F33]' : 'border-gray-100'}`}>
                <div className="flex items-center gap-2 text-[10px]">
                  <span className={`flex items-center gap-1 font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    {monthlyBookings.filter(b => b.status === 'active').length} Active
                  </span>
                  <span className={`flex items-center gap-1 font-semibold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                    <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                    {monthlyBookings.filter(b => b.status === 'pending').length} Pending
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Compact Calendar Section */}
        <div className={`${theme.card} border ${theme.border} rounded-2xl p-4`}>
          {/* Header with Year Navigation */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${isDark ? 'bg-gradient-to-br from-[#FBD282]/20 to-[#B59143]/20' : 'bg-gradient-to-br from-[#FBD282] to-[#B59143]'} flex items-center justify-center`}>
                <IconWrapper icon={FiCalendar} size={20} className={isDark ? 'text-[#FBD282]' : 'text-white'} />
              </div>
              <div>
                <h2 className={`text-base font-bold ${theme.text}`}>
                  {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h2>
                <p className={`text-[10px] ${theme.textMuted} font-semibold`}>Select date to manage assignments</p>
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center gap-2">
              {/* Year Navigation */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => changeYear(-1)}
                  className={`px-2.5 py-1.5 ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553]' : 'bg-gray-100 hover:bg-gray-200'} border ${theme.border} rounded-lg transition-all flex items-center gap-1 group`}
                  title="Previous Year"
                >
                  <IconWrapper icon={FiChevronLeft} size={12} className={`${theme.textMuted} group-hover:text-[#B59143]`} />
                  <span className={`text-[10px] font-bold ${theme.textMuted} group-hover:text-[#B59143]`}>Year</span>
                </button>
                <button
                  onClick={() => changeYear(1)}
                  className={`px-2.5 py-1.5 ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553]' : 'bg-gray-100 hover:bg-gray-200'} border ${theme.border} rounded-lg transition-all flex items-center gap-1 group`}
                  title="Next Year"
                >
                  <span className={`text-[10px] font-bold ${theme.textMuted} group-hover:text-[#B59143]`}>Year</span>
                  <IconWrapper icon={FiChevronRight} size={12} className={`${theme.textMuted} group-hover:text-[#B59143]`} />
                </button>
              </div>

              {/* Divider */}
              <div className={`w-px h-6 ${isDark ? 'bg-[#2E2F33]' : 'bg-gray-300'}`}></div>

              {/* Month Navigation */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => changeMonth(-1)}
                  className={`px-2.5 py-1.5 ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553]' : 'bg-gray-100 hover:bg-gray-200'} border ${theme.border} rounded-lg transition-all flex items-center gap-1 group`}
                  title="Previous Month"
                >
                  <IconWrapper icon={FiChevronLeft} size={12} className={`${theme.textMuted} group-hover:text-[#B59143]`} />
                  <span className={`text-[10px] font-bold ${theme.textMuted} group-hover:text-[#B59143]`}>Month</span>
                </button>
                <button
                  onClick={() => changeMonth(1)}
                  className={`px-2.5 py-1.5 ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553]' : 'bg-gray-100 hover:bg-gray-200'} border ${theme.border} rounded-lg transition-all flex items-center gap-1 group`}
                  title="Next Month"
                >
                  <span className={`text-[10px] font-bold ${theme.textMuted} group-hover:text-[#B59143]`}>Month</span>
                  <IconWrapper icon={FiChevronRight} size={12} className={`${theme.textMuted} group-hover:text-[#B59143]`} />
                </button>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'} rounded-xl border ${theme.border} p-3`}>
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 text-center mb-2">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                <div key={i} className={`text-[9px] font-black ${theme.textMuted} uppercase py-1`}>
                  {d}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth(selectedDate).map((date, idx) => {
                if (!date) return <div key={`empty-${idx}`} className="h-8" />;
                const currentStr = toLocalDateString(date);
                const isSelected = dateStr === currentStr;
                const assignmentCount = getDateAssignmentCount(currentStr);
                const isToday = toLocalDateString(new Date()) === currentStr;

                return (
                  <button
                    key={currentStr}
                    onClick={() => setSelectedDate(date)}
                    className={`
                    relative h-8 rounded-lg flex items-center justify-center transition-all duration-200 border text-[11px] font-bold
                    ${isSelected
                        ? isDark
                          ? 'bg-gradient-to-br from-[#FBD282] to-[#B59143] text-white border-[#B59143]'
                          : 'bg-gradient-to-br from-[#FBD282] to-[#B59143] text-white border-[#B59143]'
                        : isDark
                          ? 'bg-[#1A1C1F] hover:bg-[#2A3441] text-gray-300 border-[#2E2F33]'
                          : 'bg-white hover:bg-gray-100 text-gray-700 border-gray-200'
                      }
                    ${isToday && !isSelected ? 'ring-1 ring-[#B59143]/50' : ''}
                  `}
                  >
                    {date.getDate()}
                    {assignmentCount > 0 && (
                      <div className={`absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full flex items-center justify-center text-[8px] font-black ${isSelected
                          ? 'bg-white text-[#B59143]'
                          : 'bg-gradient-to-br from-[#FBD282] to-[#B59143] text-white'
                        }`}>
                        {assignmentCount}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>



      {/* Bottom Section: Assigned Floors & Add Floor Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Assigned Floors List */}
        <div className={`${theme.card} border ${theme.border} rounded-2xl p-4`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={`text-sm font-bold ${theme.text}`}>Assigned Floors</h3>
              <p className={`text-[10px] ${theme.textMuted} font-semibold`}>
                {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
            <div className={`px-2 py-1 ${isDark ? 'bg-[#2A3441]' : 'bg-gray-100'} rounded-lg border ${theme.border}`}>
              <span className={`text-xs font-black ${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'}`}>{dateAssignments.length}</span>
            </div>
          </div>

          {dateAssignments.length === 0 ? (
            <div className="text-center py-12">
              <div className={`w-16 h-16 mx-auto mb-3 rounded-xl ${isDark ? 'bg-[#2A3441]' : 'bg-gray-100'} flex items-center justify-center`}>
                <IconWrapper icon={FiInfo} size={28} className={theme.textMuted} />
              </div>
              <p className={`text-xs font-bold ${theme.text} mb-1`}>No Floors Assigned</p>
              <p className={`text-[10px] ${theme.textMuted}`}>Assign floors using the form</p>
            </div>
          ) : (
            <div className="space-y-2 overflow-y-auto">
              {dateAssignments.map(assignment => {
                const floor = floors.find(f => f.id === assignment.floorId);
                if (!floor) return null;

                return (
                  <div key={assignment.id} className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'} border ${theme.border} rounded-xl p-3`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg ${isDark ? 'bg-gradient-to-br from-[#FBD282]/20 to-[#B59143]/20' : 'bg-gradient-to-br from-[#FBD282]/30 to-[#B59143]/30'} flex items-center justify-center border ${isDark ? 'border-[#B59143]/30' : 'border-[#B59143]/50'}`}>
                        <IconWrapper icon={FiLayers} size={18} className="text-[#B59143]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-bold text-xs ${theme.text} truncate`}>{floor.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center gap-1">
                            <IconWrapper icon={FiUsers} size={10} className={theme.textMuted} />
                            <span className={`text-[10px] ${theme.text} font-semibold`}>{assignment.availableSeats}</span>
                          </div>
                          <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase ${assignment.canView
                              ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                              : isDark ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-200 text-gray-600'
                            }`}>
                            {assignment.canView ? 'View' : 'Hidden'}
                          </span>
                          <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase ${assignment.canBook
                              ? isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'
                              : isDark ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-700'
                            }`}>
                            {assignment.canBook ? 'Book' : 'View Only'}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleToggleView(assignment)}
                          className={`p-1.5 rounded-lg transition-colors border ${assignment.canView
                              ? isDark ? 'bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30' : 'bg-green-100 text-green-600 border-green-300 hover:bg-green-200'
                              : isDark ? 'bg-gray-500/20 text-gray-400 border-gray-500/30 hover:bg-gray-500/30' : 'bg-gray-200 text-gray-600 border-gray-300 hover:bg-gray-300'
                            }`}
                          title={assignment.canView ? "Hide from users" : "Show to users"}
                        >
                          <IconWrapper icon={assignment.canView ? FiEye : FiEyeOff} size={12} />
                        </button>
                        <button
                          onClick={() => handleToggleBook(assignment)}
                          className={`p-1.5 rounded-lg transition-colors border ${assignment.canBook
                              ? isDark ? 'bg-blue-500/20 text-blue-400 border-blue-500/30 hover:bg-blue-500/30' : 'bg-blue-100 text-blue-600 border-blue-300 hover:bg-blue-200'
                              : isDark ? 'bg-orange-500/20 text-orange-400 border-orange-500/30 hover:bg-orange-500/30' : 'bg-orange-100 text-orange-600 border-orange-300 hover:bg-orange-200'
                            }`}
                          title={assignment.canBook ? "Disable booking" : "Enable booking"}
                        >
                          <IconWrapper icon={assignment.canBook ? FiMousePointer : FiLock} size={12} />
                        </button>
                        <button
                          onClick={() => handleRemoveAssignment(assignment.id)}
                          className={`p-1.5 rounded-lg transition-colors border ${isDark ? 'bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30' : 'bg-red-100 text-red-600 border-red-300 hover:bg-red-200'}`}
                        >
                          <IconWrapper icon={FiTrash2} size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Add Floor Form */}
        <div className={`${theme.card} border ${theme.border} rounded-2xl p-4`}>
          <div className="flex items-center gap-2 mb-4">
            <div>
              <h3 className={`text-sm font-bold ${theme.text}`}>Assign Floor</h3>
              <p className={`text-[10px] ${theme.textMuted} font-semibold`}>Add floor to this date</p>
            </div>
          </div>

          <div className="space-y-3">
            {/* Floor Selection */}
            <div>
              <label className={`text-[9px] font-black uppercase tracking-wider ${theme.textMuted} mb-2 block`}>
                Select Floor
              </label>
              <select
                value={selectedFloorId}
                onChange={(e) => {
                  setSelectedFloorId(e.target.value);
                  const floor = floors.find(f => f.id === e.target.value);
                  setAvailableSeats(floor?.seats?.length || 0);
                }}
                className={`w-full px-3 py-2 ${isDark ? 'bg-[#14161B] text-white' : 'bg-white text-gray-900'} border ${theme.border} rounded-lg text-xs font-semibold focus:ring-1 focus:ring-[#B59143] outline-none transition-all`}
              >
                <option value="">-- Choose Floor --</option>
                {availableFloorsForDate.map(floor => (
                  <option key={floor.id} value={floor.id}>
                    {floor.name} ({floor.seats?.length || 0} seats)
                  </option>
                ))}
              </select>
              {availableFloorsForDate.length === 0 && (
                <p className={`text-[10px] ${isDark ? 'text-orange-400' : 'text-orange-600'} mt-1 font-semibold flex items-center gap-1`}>
                  <IconWrapper icon={FiInfo} size={10} />
                  All active floors assigned
                </p>
              )}
            </div>

            {/* Seat Capacity with Slider */}
            <div className={`transition-opacity ${selectedFloorId ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
              <label className={`text-[9px] font-black uppercase tracking-wider ${theme.textMuted} mb-2 block`}>
                Available Seats
              </label>
              <div className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'} border ${theme.border} rounded-lg p-3`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-2xl font-black ${isDark ? 'bg-gradient-to-r from-[#FBD282] to-[#B59143] bg-clip-text text-transparent' : 'text-[#B59143]'}`}>
                    {availableSeats}
                  </span>
                  <span className={`text-[9px] ${theme.textMuted} font-bold uppercase`}>
                    of {maxSeats}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max={maxSeats}
                  value={availableSeats}
                  onChange={(e) => setAvailableSeats(parseInt(e.target.value))}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #B59143 0%, #B59143 ${(availableSeats / maxSeats) * 100}%, ${isDark ? '#2E2F33' : '#E5E7EB'} ${(availableSeats / maxSeats) * 100}%, ${isDark ? '#2E2F33' : '#E5E7EB'} 100%)`
                  }}
                />
                <div className="flex gap-1 mt-2">
                  <button
                    onClick={() => setAvailableSeats(Math.max(0, availableSeats - 1))}
                    className={`flex-1 py-1.5 ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553]' : 'bg-gray-100 hover:bg-gray-200'} border ${theme.border} rounded-lg text-xs font-black transition-colors`}
                  >
                    −
                  </button>
                  <button
                    onClick={() => setAvailableSeats(Math.min(maxSeats, availableSeats + 1))}
                    className={`flex-1 py-1.5 ${isDark ? 'bg-[#2A3441] hover:bg-[#3A4553]' : 'bg-gray-100 hover:bg-gray-200'} border ${theme.border} rounded-lg text-xs font-black transition-colors`}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Visibility Settings */}
            <div className={`transition-opacity ${selectedFloorId ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
              <label className={`text-[9px] font-black uppercase tracking-wider ${theme.textMuted} mb-2 block`}>
                User Permissions
              </label>
              <div className="space-y-2">
                {/* Can View Toggle */}
                <div className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'} border ${theme.border} rounded-lg p-2.5`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${canView ? (isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600') : (isDark ? 'bg-gray-500/20 text-gray-400' : 'bg-gray-200 text-gray-600')}`}>
                        <IconWrapper icon={canView ? FiEye : FiEyeOff} size={14} />
                      </div>
                      <div>
                        <p className={`font-bold text-[11px] ${theme.text}`}>Users Can See</p>
                        <p className={`text-[9px] ${theme.textMuted}`}>View availability table</p>
                      </div>
                    </div>
                    <Switch
                      colorScheme="green"
                      size="sm"
                      isChecked={canView}
                      onChange={(e) => setCanView(e.target.checked)}
                    />
                  </div>
                </div>

                {/* Can Book Toggle */}
                <div className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'} border ${theme.border} rounded-lg p-2.5`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${canBook ? (isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600') : (isDark ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-600')}`}>
                        <IconWrapper icon={canBook ? FiMousePointer : FiLock} size={14} />
                      </div>
                      <div>
                        <p className={`font-bold text-[11px] ${theme.text}`}>Users Can Book</p>
                        <p className={`text-[9px] ${theme.textMuted}`}>Click and book seats</p>
                      </div>
                    </div>
                    <Switch
                      colorScheme="blue"
                      size="sm"
                      isChecked={canBook}
                      onChange={(e) => setCanBook(e.target.checked)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleAddAssignment}
              disabled={!selectedFloorId || availableSeats <= 0}
              className={`w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#FBD282] to-[#B59143] text-white font-black text-[11px] uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed ${!selectedFloorId || availableSeats <= 0 ? '' : 'hover:from-[#E8C16C] hover:to-[#A37F33] active:scale-[0.98]'}`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <IconWrapper icon={FiPlus} size={14} />
                Assign to {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorAssignmentManager;


